import React, { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Home, Trophy, Users, ClipboardList, Calendar, 
  FileText, ChevronLeft, Package, BarChart3,
  UserCheck, Shield, Plane, Warehouse, ListChecks,
  PlusCircle, Timer, ChevronDown
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const DemoLayout: React.FC = () => {
  const location = useLocation();

  const DEMO_EVENT_ID = 'f83c2769-b4aa-4ba9-9e3e-5e205fd40047';

  const primaryNav = [
    { path: '/demo/hub', label: 'Hub', icon: Home },
    { path: '/demo/create-event', label: 'Create Event', icon: PlusCircle },
    { path: `/demo/event-management/${DEMO_EVENT_ID}`, label: 'Event Mgmt', icon: Calendar },
    { path: '/demo/registrations', label: 'Registrations', icon: Users },
    { path: '/demo/tournaments', label: 'Tournaments', icon: Trophy },
    { path: `/demo/scoring/${DEMO_EVENT_ID}`, label: 'Scoring', icon: FileText },
    { path: '/demo/scheduling', label: 'Scheduling', icon: Timer },
    { path: '/demo/leaderboards', label: 'Rankings', icon: BarChart3 },
  ];

  const opsNav = [
    { path: '/demo/staffing', label: 'Staffing', icon: UserCheck },
    { path: '/demo/officials', label: 'Officials', icon: Shield },
    { path: '/demo/scorekeepers', label: 'Scorekeepers', icon: ClipboardList },
    { path: '/demo/travel', label: 'Travel', icon: Plane },
    { path: '/demo/inventory', label: 'Inventory', icon: Warehouse },
    { path: '/demo/checklists', label: 'Checklists', icon: ListChecks },
  ];

  const moreNav = [
    { path: '/demo/check-in', label: 'Check-In', icon: ClipboardList },
    { path: '/demo/packets', label: 'Packets', icon: Package },
  ];

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Demo Banner Header */}
      <div className="bg-gradient-to-r from-yellow-400 to-amber-500 text-yellow-900 px-4 py-3 sticky top-0 z-50 shadow-md">
        <div className="container mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="bg-white/90 text-yellow-800 border-yellow-600 font-bold">
              🎮 DEMO MODE
            </Badge>
            <span className="font-medium hidden sm:inline">
              Sales Demo Environment - No real data affected
            </span>
          </div>
          <Link to="/demo/hub">
            <Button variant="outline" size="sm" className="bg-white/90 hover:bg-white border-yellow-600">
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back to Hub
            </Button>
          </Link>
        </div>
      </div>

      {/* Navigation Bar */}
      <div className="bg-white border-b shadow-sm">
        <div className="container mx-auto">
          <nav className="flex items-center gap-1 py-2 overflow-x-auto">
            {primaryNav.map((item) => (
              <Link key={item.path} to={item.path}>
                <Button
                  variant={isActive(item.path) ? "default" : "ghost"}
                  size="sm"
                  className={isActive(item.path) ? "bg-primary" : ""}
                >
                  <item.icon className="w-4 h-4 mr-1" />
                  {item.label}
                </Button>
              </Link>
            ))}

            {/* Operations Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant={opsNav.some(i => isActive(i.path)) ? "default" : "ghost"}
                  size="sm"
                >
                  <Warehouse className="w-4 h-4 mr-1" />
                  Operations
                  <ChevronDown className="w-3 h-3 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {opsNav.map((item) => (
                  <Link key={item.path} to={item.path}>
                    <DropdownMenuItem className="cursor-pointer">
                      <item.icon className="w-4 h-4 mr-2" />
                      {item.label}
                    </DropdownMenuItem>
                  </Link>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* More Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant={moreNav.some(i => isActive(i.path)) ? "default" : "ghost"}
                  size="sm"
                >
                  More
                  <ChevronDown className="w-3 h-3 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {moreNav.map((item) => (
                  <Link key={item.path} to={item.path}>
                    <DropdownMenuItem className="cursor-pointer">
                      <item.icon className="w-4 h-4 mr-2" />
                      {item.label}
                    </DropdownMenuItem>
                  </Link>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <main className="container mx-auto py-6 px-4">
        <Outlet />
      </main>

      {/* Demo Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-yellow-100 border-t border-yellow-300 py-2 px-4 text-center text-sm text-yellow-800">
        <span className="font-medium">Demo Mode Active</span> - All actions are simulated and won't affect production data
      </div>
    </div>
  );
};

export default DemoLayout;
